import moment from 'moment';

/**
 * Calendar Utility Functions
 * Helper functions for calendar calculations and attendance mapping
 */

/**
 * Get all days for a calendar month grid (including prev/next month days)
 * @param {number} month - Month (1-12)
 * @param {number} year - Year (YYYY)
 * @returns {Array<Object>} Array of day objects with date and isCurrentMonth flag
 */
export const getCalendarDays = (month, year) => {
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const daysInMonth = lastDay.getDate();
  const startDayOfWeek = firstDay.getDay(); // 0 = Sunday

  const days = [];

  // Previous month days to fill the first week
  if (startDayOfWeek > 0) {
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const prevMonthLastDay = new Date(prevYear, prevMonth, 0).getDate();

    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(prevYear, prevMonth - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
        isPrevMonth: true
      });
    }
  }

  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      date: new Date(year, month - 1, i),
      isCurrentMonth: true,
      isPrevMonth: false
    });
  }

  // Next month days to complete the grid (6 rows x 7 days = 42 cells)
  const remainingDays = 42 - days.length;
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;

  for (let i = 1; i <= remainingDays; i++) {
    days.push({
      date: new Date(nextYear, nextMonth - 1, i),
      isCurrentMonth: false,
      isPrevMonth: false
    });
  }

  return days;
};

/**
 * Map attendance records to calendar days
 * @param {Array<Object>} calendarDays - Array of calendar day objects
 * @param {Array<Object>} attendanceRecords - Array of attendance records
 * @returns {Array<Object>} Calendar days with attendance data
 */
export const mapAttendanceToCalendar = (calendarDays, attendanceRecords, festivals = []) => {
  // Create a map for quick lookup
  const attendanceMap = new Map();
  const festivalMap = new Map();

  // Map festivals by YYYY-MM-DD
  (festivals || []).forEach(f => {
    if (f.date) {
      const dateKey = typeof f.date === 'string' ? f.date.split('T')[0] : moment(f.date).format('YYYY-MM-DD');
      festivalMap.set(dateKey, f);
    }
  });

  attendanceRecords.forEach(record => {
    // Handle different date formats
    let dateKey;
    if (record.date && record.date._seconds) {
      // 1. Priority: Firebase Timestamp (Reliable)
      dateKey = moment.unix(record.date._seconds).format('YYYY-MM-DD');
    } else if (record.date && record.date.seconds) {
      // 1b. Priority: Firebase Timestamp
      dateKey = moment.unix(record.date.seconds).format('YYYY-MM-DD');
    } else if (record.date && (record.date instanceof Date || (typeof record.date === 'string' && record.date.includes('T')))) {
      // 2. Priority: Date Object or ISO String (Reliable)
      dateKey = moment(record.date).format('YYYY-MM-DD');
    } else if (record.dateString) {
      // 3. Priority: Pre-formatted dateString (Legacy / Upload fallback)
      dateKey = record.dateString;
    } else {
      // Fallback
      dateKey = moment(record.date).format('YYYY-MM-DD');
    }

    if (attendanceMap.has(dateKey)) {
      const existing = attendanceMap.get(dateKey);
      const isRecordExtra = record.status === 'Extra Work' || record.isExtraWork;
      const isExistingExtra = existing.status === 'Extra Work' || existing.isExtraWork;
      const hasPunch = (v) => v && v !== '-' && v !== '' && /[1-9]/.test(String(v));

      if (isRecordExtra && !isExistingExtra) {
        attendanceMap.set(dateKey, {
          ...existing,
          extraWork: record,
          hasExtraWork: true
        });
      } else if (!isRecordExtra && isExistingExtra) {
        attendanceMap.set(dateKey, {
          ...record,
          extraWork: existing,
          hasExtraWork: true
        });
      } else {
        const recordPunchCount = (hasPunch(record.inTime) ? 1 : 0) + (hasPunch(record.outTime) ? 1 : 0);
        const existingPunchCount = (hasPunch(existing.inTime) ? 1 : 0) + (hasPunch(existing.outTime) ? 1 : 0);
        if (recordPunchCount >= existingPunchCount) {
          attendanceMap.set(dateKey, record);
        }
      }
    } else {
      attendanceMap.set(dateKey, record);
    }
  });

  // Map attendance to calendar days
  return calendarDays.map(day => {
    const dateKey = moment(day.date).format('YYYY-MM-DD');
    const today = moment().startOf('day');
    const isPastOrToday = moment(day.date).isSameOrBefore(today, 'day');
    const dayOfWeek = day.date.getDay();
    const festival = festivalMap.get(dateKey) || null;

    let record = attendanceMap.get(dateKey) || null;
    let finalAttendance = record ? { ...record } : null;

    if (finalAttendance) {
      // 1. Flexible punch detection
      const hasPunch = (v) => v && v !== '-' && v !== '' && /[1-9]/.test(String(v));
      const hasIn = hasPunch(finalAttendance.inTime);
      const hasOut = hasPunch(finalAttendance.outTime);

      // 2. Normalize Status (same logic as summary)
      let currentStatus = normalizeStatus(finalAttendance.status);
      
      // 3. Priority Logic: Explicit statuses (Leave, Half-day, Holiday, Extra Work) override everything
      if (currentStatus === 'Leave' || currentStatus === 'Half-day' || currentStatus === 'Holiday' || currentStatus === 'Extra Work') {
        finalAttendance.status = currentStatus;
      } 
      // 4. Festival check (Except Tuesday): If festival day and no punches or status is Absent -> Festival (paid leave)
      else if (festival && dayOfWeek !== 2 && ((!hasIn && !hasOut) || currentStatus === 'Absent')) {
        finalAttendance.status = 'Festival';
      }
      // 5. Weekly Off Rule: Default to Weekly Off on Tuesday (day 2) ONLY if no punches
      else if (dayOfWeek === 2 && !hasIn && !hasOut) {
        finalAttendance.status = 'W.Off';
      }
      // 6. Punch-based override for standard working days (including Tuesdays with punches)
      else if ((hasIn && !hasOut) || (!hasIn && hasOut)) {
        finalAttendance.status = 'Miss-Punch';
      } else if (!hasIn && !hasOut) {
        // Special case: Tuesday without punches becomes W.Off, festival becomes Festival (except Tuesday), other days Absent
        finalAttendance.status = (festival && dayOfWeek !== 2) ? 'Festival' : (dayOfWeek === 2 ? 'W.Off' : 'Absent');
      } else {
        // Both punches present
        const inTime = moment(finalAttendance.inTime, ["HH:mm", "h:mm A", "H:mm"]);
        const outTime = moment(finalAttendance.outTime, ["HH:mm", "h:mm A", "H:mm"]);
        
        if (inTime.isValid() && outTime.isValid()) {
          const durationMins = outTime.diff(inTime, 'minutes');
          const inMinutes = inTime.hours() * 60 + inTime.minutes();
          const outMinutes = outTime.hours() * 60 + outTime.minutes();
          
          // Set working hours and overtime hours for the modal
          finalAttendance.workingHours = durationMins > 0 ? (durationMins / 60).toFixed(2) : '0.00';
          
          const otThreshold = 18 * 60 + 30; // 6:30 PM
          if (outMinutes > otThreshold) {
            finalAttendance.overtimeHours = ((outMinutes - otThreshold) / 60).toFixed(2);
          } else {
            finalAttendance.overtimeHours = '0.00';
          }

          if (durationMins > 0 && durationMins < 300) {
            finalAttendance.status = 'Half-day';
          } else if (inMinutes > (9 * 60 + 35)) {
            finalAttendance.status = 'Late';
          } else if (outMinutes >= (17 * 60) && outMinutes < (18 * 60 + 30)) {
            finalAttendance.status = 'EarlyGoing';
          } else {
            finalAttendance.status = 'Present';
          }
        } else {
          finalAttendance.status = 'Present';
          finalAttendance.workingHours = '0.00';
          finalAttendance.overtimeHours = '0.00';
        }
      }
    } else if (festival && dayOfWeek !== 2) {
      // Missing record on a festival day (not Tuesday) -> Festival (paid leave)
      finalAttendance = {
        status: 'Festival',
        inTime: '-',
        outTime: '-',
        festival
      };
    } else if (festival && dayOfWeek === 2) {
      // Festival falling on Tuesday -> Company Weekly Off
      finalAttendance = {
        status: 'W.Off',
        inTime: '-',
        outTime: '-',
        festival
      };
    } else if (day.isCurrentMonth && isPastOrToday) {
      // 3. Automated status for missing records in current month
      finalAttendance = {
        status: dayOfWeek === 2 ? 'W.Off' : 'Absent',
        inTime: '-',
        outTime: '-'
      };
    }

    if (festival && finalAttendance) {
      finalAttendance.festival = festival;
    }

    return {
      ...day,
      festival,
      attendance: finalAttendance,
      isToday: moment(day.date).isSame(moment(), 'day')
    };
  });
};

/**
 * Normalize attendance status to standard categories
 * @param {string} status - Raw status string
 * @returns {string} Standardized status
 */
export const normalizeStatus = (status) => {
  if (!status) return 'default';

  const s = String(status).trim().toUpperCase();

  if (s === 'PRESENT' || s === 'P' || s === 'PRES') return 'Present';
  if (s.startsWith('ABSENT') || s === 'A' || s === 'ABS') return 'Absent';
  if (s === 'LEAVE' || s === 'L' || s === 'LEV') return 'Leave';
  if (s === 'LATE' || s === 'LATE MARK') return 'Late';
  if (s.includes('HALF') || s.includes('1/2') || s === 'HD') return 'Half-day';
  if (s === 'WEEKLYOFF' || s === 'WO' || s === 'W' || s === 'WEEKLY OFF' || s === 'W.OFF') return 'WeeklyOff';
  if (s === 'HOLIDAY' || s === 'H' || s === 'HOL') return 'Holiday';
  if (s === 'FESTIVAL' || s === 'FEST' || s === 'F') return 'Festival';
  if (s === 'MISS-PUNCH' || s === 'MISSPUNCH' || s === 'MP') return 'Miss-Punch';
  if (s === 'EARLYGOING' || s === 'EARLY EXIT' || s === 'E') return 'EarlyGoing';
  if (s === 'EXTRA WORK' || s === 'EXTRAWORK' || s === 'EW') return 'Extra Work';

  return 'default';
};

/**
 * Get background color for attendance status
 * @param {string} status - Attendance status
 * @returns {string} CSS color value
 */
export const getAttendanceColor = (status) => {
  const normalizedStatus = normalizeStatus(status);
  const colors = {
    'Present': '#d4edda',      // Light green
    'Absent': '#f8d7da',       // Light red
    'Leave': '#fff3cd',        // Light yellow
    'Half-day': '#fff7ed',     // Faint orange
    'Late': '#fff9c4',         // Yellow
    'WeeklyOff': '#e2e3e5',    // Light gray
    'Holiday': '#d1ecf1',      // Light blue
    'Festival': '#fff0f5',     // Light pastel rainbow base
    'Miss-Punch': '#e7f1ff',    // Light blue background
    'EarlyGoing': '#f5dbff',   // Light purple background
    'Extra Work': '#e0f2fe',   // Light sky blue
    'default': '#ffffff'       // White
  };

  return colors[normalizedStatus] || colors.default;
};

/**
 * Get border color for attendance status (darker shade)
 * @param {string} status - Attendance status
 * @returns {string} CSS color value
 */
export const getAttendanceBorderColor = (status) => {
  const normalizedStatus = normalizeStatus(status);
  const colors = {
    'Present': '#28a745',      // Green
    'Absent': '#dc3545',       // Red
    'Leave': '#ffc107',        // Yellow
    'Half-day': '#f97316',     // Orange
    'Late': '#fbc02d',         // Darker yellow/gold
    'WeeklyOff': '#6c757d',    // Gray
    'Holiday': '#17a2b8',      // Blue
    'Festival': '#ff4b4b',     // Rainbow red primary
    'Miss-Punch': '#0d6efd',    // Standard Blue
    'EarlyGoing': '#c217e8',   // Purple
    'Extra Work': '#0dcaf0',   // Cyan
    'default': '#dee2e6'       // Light gray
  };

  return colors[normalizedStatus] || colors.default;
};

/**
 * Get text color for attendance status (for contrast)
 * @param {string} status - Attendance status
 * @returns {string} CSS color value
 */
export const getAttendanceTextColor = (status) => {
  // Most statuses use dark text, but some need light text
  const lightTextStatuses = [];
  return lightTextStatuses.includes(status) ? '#ffffff' : '#212529';
};

/**
 * Format date for display
 * @param {Date} date - Date object
 * @param {string} format - Moment format string
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'DD MMM YYYY') => {
  return moment(date).format(format);
};

/**
 * Get month name
 * @param {number} month - Month (1-12)
 * @returns {string} Month name
 */
export const getMonthName = (month) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[month - 1];
};

/**
 * Get short month name
 * @param {number} month - Month (1-12)
 * @returns {string} Short month name
 */
export const getShortMonthName = (month) => {
  return getMonthName(month).substring(0, 3);
};

/**
 * Get day names for calendar header
 * @returns {Array<string>} Array of day names
 */
export const getDayNames = () => {
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
};

/**
 * Check if two dates are the same day
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean} True if same day
 */
export const isSameDay = (date1, date2) => {
  return moment(date1).isSame(moment(date2), 'day');
};

/**
 * Get previous month and year
 * @param {number} month - Current month (1-12)
 * @param {number} year - Current year
 * @returns {Object} { month, year }
 */
export const getPreviousMonth = (month, year) => {
  if (month === 1) {
    return { month: 12, year: year - 1 };
  }
  return { month: month - 1, year };
};

/**
 * Get next month and year
 * @param {number} month - Current month (1-12)
 * @param {number} year - Current year
 * @returns {Object} { month, year }
 */
export const getNextMonth = (month, year) => {
  if (month === 12) {
    return { month: 1, year: year + 1 };
  }
  return { month: month + 1, year };
};

/**
 * Get current month and year
 * @returns {Object} { month, year }
 */
export const getCurrentMonthYear = () => {
  const now = new Date();
  return {
    month: now.getMonth() + 1,
    year: now.getFullYear()
  };
};

/**
 * Calculate attendance percentage
 * @param {number} presentDays - Number of present days
 * @param {number} totalWorkingDays - Total working days (excluding weeklyoffs and holidays)
 * @returns {number} Percentage (0-100)
 */
export const calculateAttendancePercentage = (presentDays, totalWorkingDays) => {
  if (totalWorkingDays === 0) return 0;
  return Math.round((presentDays / totalWorkingDays) * 100);
};

/**
 * Get year range for dropdown
 * @param {number} currentYear - Current year
 * @param {number} range - Years before and after current year
 * @returns {Array<number>} Array of years
 */
export const getYearRange = (currentYear, range = 5) => {
  const years = [];
  for (let i = currentYear - range; i <= currentYear + range; i++) {
    years.push(i);
  }
  return years;
};
